import {isNil, isNotNil} from 'ramda';

import type {BusinessCaseResponseBody} from '@dms/api/sales';

import type {
  BusinessCaseFacts,
  BusinessCaseMoney,
  BusinessCasePurchaseVehicle,
  BusinessCaseSaleVehicle,
} from '@prompt-bridge/shared/ai-assistant-core';

import type {
  AssistantBusinessCaseVehicleSource,
  ToBusinessCaseFactsInput,
} from '../types/ToBusinessCaseFactsInput';
import {toBoundedAssistantBusinessCaseText} from './toBoundedAssistantBusinessCaseText';
import {toResolvedBusinessCaseVehicleLabel} from './toResolvedBusinessCaseVehicleLabel';

const toMoney = (
  money: {amount: string; currency: string} | null | undefined
): BusinessCaseMoney | undefined => {
  if (isNil(money)) {
    return undefined;
  }

  const amount = Number(money.amount);
  return Number.isFinite(amount) ? {amount, currency: money.currency} : undefined;
};

const toSaleVehicle = (
  businessCase: BusinessCaseResponseBody,
  vehicle: AssistantBusinessCaseVehicleSource | null,
  saleVehicle: ToBusinessCaseFactsInput['saleVehicle']
): BusinessCaseSaleVehicle | undefined => {
  const offerSaleVehicle = businessCase.offers[0]?.saleVehicles?.[0];
  if (isNil(offerSaleVehicle)) {
    return undefined;
  }

  const make = toResolvedBusinessCaseVehicleLabel(vehicle?.make);
  const model = toResolvedBusinessCaseVehicleLabel(vehicle?.modelFamily);
  const registrationPlate = toBoundedAssistantBusinessCaseText(vehicle?.state?.registrationPlate);
  const vin = toBoundedAssistantBusinessCaseText(vehicle?.vin);
  const priceWithVat = toMoney(offerSaleVehicle.sellingPrice?.withVat);
  const priceWithoutVat = toMoney(offerSaleVehicle.sellingPrice?.withoutVat);
  const discountAmount = toMoney(
    offerSaleVehicle.discountPrice?.withVat ?? offerSaleVehicle.discountPrice?.withoutVat
  );
  const discountPercentage = toBoundedAssistantBusinessCaseText(
    offerSaleVehicle.discountPercentage
  );

  const saleState = vehicle?.saleState;
  const isReservedByThisCase = saleVehicle?.activeReservation?.businessCaseId === businessCase.id;
  const isReserved =
    isNotNil(saleVehicle) &&
    isNotNil(saleVehicle.activeReservation) &&
    saleVehicle.activeReservation.businessCaseId !== businessCase.id;

  return {
    vehicleId: offerSaleVehicle.vehicleId,
    ...(isNotNil(make) ? {make} : {}),
    ...(isNotNil(model) ? {model} : {}),
    ...(isNotNil(vehicle?.manufacturedOnYear) ? {year: vehicle.manufacturedOnYear} : {}),
    ...(isNotNil(registrationPlate) ? {registrationPlate} : {}),
    ...(isNotNil(vin) ? {vin} : {}),
    ...(isNotNil(priceWithVat) ? {priceWithVat} : {}),
    ...(isNotNil(priceWithoutVat) ? {priceWithoutVat} : {}),
    ...(isNotNil(discountAmount) ? {discountAmount} : {}),
    ...(isNotNil(discountPercentage) ? {discountPercentage} : {}),
    ...(isNotNil(saleState)
      ? {
          isAvailable:
            saleState === 'TO_SELL' ||
            (saleState === 'RESERVED' && (isNil(saleVehicle) || isReservedByThisCase)),
        }
      : {}),
    ...(isNotNil(saleVehicle) || isNotNil(saleState) ? {isReserved} : {}),
  };
};

const toPurchaseVehicle = (
  businessCase: BusinessCaseResponseBody
): BusinessCasePurchaseVehicle | undefined => {
  const purchaseVehicle = businessCase.offers[0]?.purchaseVehicles?.[0];
  const brokerageVehicle = businessCase.offers[0]?.purchaseBrokerageVehicles?.[0];
  const source = purchaseVehicle ?? brokerageVehicle;
  if (isNil(source)) {
    return undefined;
  }

  const customerExpectedPrice = toMoney(source.costs.customerRequestedPrice);
  const maxPurchasePrice = toMoney(
    source.pricing.maxBuyingPrice.priceWithVat ??
      source.pricing.maxBuyingPrice.priceNotDeductible ??
      source.pricing.maxBuyingPrice.priceWithoutVat
  );
  const expectedPurchaseOn = toBoundedAssistantBusinessCaseText(source.expectedPurchaseOn);

  return {
    vehicleId: source.vehicleId,
    tradeType: purchaseVehicle?.purchaseVehicleType ?? 'BROKERAGE',
    ...(isNotNil(expectedPurchaseOn) ? {expectedPurchaseOn} : {}),
    ...(isNotNil(customerExpectedPrice) ? {customerExpectedPrice} : {}),
    ...(isNotNil(maxPurchasePrice) ? {maxPurchasePrice} : {}),
  };
};

export const toBusinessCaseVehicles = (
  businessCase: BusinessCaseResponseBody,
  vehicle: AssistantBusinessCaseVehicleSource | null,
  canReadMargin: boolean,
  saleVehicleSource?: ToBusinessCaseFactsInput['saleVehicle']
): Pick<BusinessCaseFacts, 'margin' | 'purchaseVehicle' | 'saleVehicle'> => {
  const saleVehicle = toSaleVehicle(businessCase, vehicle, saleVehicleSource);
  const purchaseVehicle = toPurchaseVehicle(businessCase);
  const margin = canReadMargin
    ? toMoney(
        businessCase.offers[0]?.totalMarginPrice?.withVat ??
          businessCase.offers[0]?.totalMarginPrice?.withoutVat
      )
    : undefined;

  return {
    ...(isNotNil(saleVehicle) ? {saleVehicle} : {}),
    ...(isNotNil(purchaseVehicle) ? {purchaseVehicle} : {}),
    ...(isNotNil(margin) ? {margin} : {}),
  };
};
